import React from 'react';
import { TechnicianInfo } from '../../../entities/user';
import { isWorkingDay } from '../../../shared/lib/workingDays';
import { BOARD_WIDTH } from '../../../shared/lib/schedule';
import { BOARD_LAYER } from '../model/boardLayers';
import type { DragPreview, PlanningDraft } from '../model/boardPreview';
import type { DragHandle } from '../model/buildSchedulePatch';
import { DRAFT_KEY } from '../model/dragSubject';
import { MUTED_TINT } from '../model/mutedTint';
import type { TechnicianTrade } from '../model/technicianTrades';
import { resolveDragPolicy, type DragContext } from '../model/dragPolicy';
import { ROW_TECHNICIAN_ATTR } from '../model/useBlockDrag';
import type { BoardBlock, BoardRowModel } from '../model/boardTypes';
import type { SegmentHandle } from '../../../shared/lib/schedule';
import { BoardHourGrid } from './BoardHourGrid';
import { BoardShiftOverlay } from './BoardShiftOverlay';
import { DragGhost } from './DragGhost';
import { PlanningDraftBlock } from './PlanningDraftBlock';
import { ScheduleBlock } from './ScheduleBlock';

/**
 * The schedule payload carries no roles, and every technician on this board is a Handwerker —
 * which is exactly what `getPositionsFromRoles` renders for `WORKER`.
 */
const TECHNICIAN_ROLES = ['WORKER'] as const;

interface Props {
  row: BoardRowModel;
  dayIso: string;
  dragContext: DragContext;
  preview: DragPreview | null;
  onDragStart: (block: BoardBlock, handle: DragHandle, e: React.PointerEvent) => void;
  wasDragged: () => boolean;
  /**
   * A click on the track picks a slot. Planning mode, and only while no form is open on top of it —
   * with the docked form beside the board, a click meant to bring the calendar into focus must not
   * teleport the block being edited.
   */
  pickable: boolean;
  /**
   * Blocks are display-only: reading the day is why you are here, but the click belongs to what is
   * being placed. True in planning mode and whenever an upsert form is open.
   */
  blocksInert: boolean;
  /**
   * The block the open form is editing. Dimmed, because the live draft stands in for it — the same
   * treatment a block gets while it is being dragged.
   */
  dimmedKey?: string | null;
  /** A drag is live anywhere on the board — blocks suppress their hover cards for its duration. */
  dragActive: boolean;
  /** Width of the docked upsert panel, or 0. Keeps a hover card out from under it. */
  reservedRightPx: number;
  draft: PlanningDraft | null;
  /** `offsetPx` is measured from the track's left edge, i.e. board minute 0. */
  onPickSlot: (technicianId: number, offsetPx: number) => void;
  /** A drag starting on the draft block or one of its handles. */
  onDraftDragStart: (handle: 'move' | SegmentHandle, e: React.PointerEvent) => void;
  /** The Handwerker does not cover the Gewerk being planned — set back visually, still plannable. */
  muted?: boolean;
  /** The Handwerker's Gewerke as chips, the planned one first. Empty before the labels load. */
  trades?: TechnicianTrade[];
  /** Order-editor controls for the technician card — only while the Leitung arranges the calendar. */
  actions?: React.ReactNode;
}

export const BoardRow = ({
  row,
  dayIso,
  dragContext,
  preview,
  onDragStart,
  wasDragged,
  pickable,
  blocksInert,
  dimmedKey,
  dragActive,
  reservedRightPx,
  draft,
  onPickSlot,
  onDraftDragStart,
  muted = false,
  trades,
  actions,
}: Props) => (
  <div className="flex" style={{ height: row.rowHeight }} data-muted={muted || undefined}>
    {/*
      The sticky column. `flex` so the card inside can stretch to the row's height, which is set from
      `rowHeightPx` and grows with the lane count.
    */}
    <div className={`sticky left-0 flex shrink-0 ${BOARD_LAYER.technicianColumn}`}>
      <TechnicianInfo
        technicianId={String(row.technicianId)}
        technicianName={row.technicianName}
        technicianRoles={[...TECHNICIAN_ROLES]}
        technicianAvatar={row.technicianAvatar}
        muted={muted}
        trades={trades}
        actions={actions}
      />
    </div>

    <div
      // Read by the drag primitive's `elementFromPoint` hit test to resolve the hovered row.
      {...{ [ROW_TECHNICIAN_ATTR]: row.technicianId }}
      // `nativeEvent.offsetX` would be relative to whatever child was hit — a gridline, a block —
      // so the offset is measured against this track's own box.
      onClick={
        pickable
          ? (e) => {
              // A draft drag ends with a trailing click on the track — placing here would teleport
              // the draft to wherever the pointer was released.
              if (wasDragged()) return;
              onPickSlot(
                row.technicianId,
                e.clientX - e.currentTarget.getBoundingClientRect().left,
              );
            }
          : undefined
      }
      className={`relative shrink-0 border-b border-slate-200 bg-white ${
        pickable ? 'cursor-copy' : ''
      }`}
      style={{ width: BOARD_WIDTH }}
    >
      <BoardHourGrid />

      <BoardShiftOverlay
        working={isWorkingDay(row.workingDays, dayIso)}
        shiftStart={row.shiftStart}
        shiftEnd={row.shiftEnd}
      />

      {row.blocks.map((block) => (
        <ScheduleBlock
          key={block.key}
          block={block}
          policy={resolveDragPolicy(block, dragContext)}
          isDragging={preview?.blockKey === block.key || block.key === dimmedKey}
          onDragStart={onDragStart}
          wasDragged={wasDragged}
          inert={blocksInert}
          dragActive={dragActive}
          reservedRightPx={reservedRightPx}
        />
      ))}

      {/*
        Painted after the blocks and before the ghost and draft (`BOARD_LAYER.ghost`), so the row reads
        as set back while the slot being placed stays crisp. `pointer-events-none`: picking a slot, the
        drag hit test and the hover cards all still reach what is underneath.
      */}
      {muted && <div aria-hidden className={`pointer-events-none absolute inset-0 ${MUTED_TINT}`} />}

      {/* The ghost belongs to the row the pointer is over, which may not be the block's own. */}
      {preview && preview.technicianId === row.technicianId && (
        <DragGhost
          layout={preview.layout}
          lane={preview.lane}
          colliding={preview.colliding}
          outsideShift={preview.outsideShift}
        />
      )}

      {/* The planning draft — a first-class block riding the same drag primitive as the rest. */}
      {draft && draft.technicianId === row.technicianId && (
        <PlanningDraftBlock
          draft={draft.state}
          layout={draft.layout}
          colliding={draft.colliding}
          outsideShift={draft.outsideShift}
          isDragging={preview?.blockKey === DRAFT_KEY}
          onDragStart={onDraftDragStart}
        />
      )}
    </div>
  </div>
);
